import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Flame, Heart, Sparkles } from "lucide-react";
import { AppleIcon, GooglePlayIcon } from "@/components/StoreIcons";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PhoneMockup = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative [perspective:1200px]">
      <motion.div
        data-testid="live-preview-phone"
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 80, rotateY: -14 }}
        animate={{ opacity: 1, y: 0, rotateY: 0 }}
        transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="phone-frame relative w-[270px] sm:w-[300px] mx-auto"
      >
        <div className="p-2.5 bg-black rounded-[2.5rem]">
          <div className="relative aspect-[9/18.6] overflow-hidden rounded-[2rem]">
            <img src="/assets/app-6.webp" alt="IN-It app — Who's IN right now live feed" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-[26px] bg-black rounded-full" />
            <div className="pointer-events-none absolute inset-0 rounded-[2rem] shadow-[inset_0_0_28px_rgba(0,0,0,0.5)]" />
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ transform: "translateZ(60px)" }}
        className="absolute -left-8 top-16 animate-float"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.1, duration: 0.8 }}
      >
        <div className="glass rounded-full px-4 py-2 flex items-center gap-2">
          <span className="relative flex w-2.5 h-2.5">
            <span className="live-dot relative inline-flex rounded-full w-2.5 h-2.5 bg-ink-ember" />
          </span>
          <span className="font-mono text-[11px] tracking-widest text-white">6 IN THE ROOM</span>
        </div>
      </motion.div>

      <motion.div
        className="absolute -right-6 top-1/3 animate-float-delayed"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.3, duration: 0.8 }}
      >
        <div className="rounded-full px-4 py-2 bg-ink-green/15 border border-ink-green/40 backdrop-blur-md">
          <span className="font-mono text-[11px] tracking-widest text-ink-green">TAP TO ENTER</span>
        </div>
      </motion.div>

      <motion.div
        className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <button data-testid="live-react-button-fire" className="glass rounded-full p-3 hover:border-ink-coral/60 transition-colors duration-200 group">
          <Flame className="w-4 h-4 text-ink-coral group-hover:scale-125 transition-transform duration-200" />
        </button>
        <button data-testid="live-react-button-heart" className="glass rounded-full p-3 hover:border-ink-pink/60 transition-colors duration-200 group">
          <Heart className="w-4 h-4 text-ink-pink group-hover:scale-125 transition-transform duration-200" />
        </button>
        <button data-testid="live-react-button-sparkle" className="glass rounded-full p-3 hover:border-ink-cyan/60 transition-colors duration-200 group">
          <Sparkles className="w-4 h-4 text-ink-cyan group-hover:scale-125 transition-transform duration-200" />
        </button>
      </motion.div>
    </div>
  );
};

export const HeroSection = () => {
  const glowX = useMotionValue(0);
  const glowY = useMotionValue(0);
  const sx = useSpring(glowX, { stiffness: 60, damping: 20 });
  const sy = useSpring(glowY, { stiffness: 60, damping: 20 });

  return (
    <section
      id="experience"
      className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-24"
      onMouseMove={(e) => { glowX.set(e.clientX - 250); glowY.set(e.clientY - 250); }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <img src="/assets/app-4.webp" alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-[0.14] [mask-image:linear-gradient(to_left,black,transparent)]" />
        <div className="absolute -top-40 left-[10%] w-[500px] h-[500px] rounded-full bg-[#FF6B00]/20 blur-[140px]" />
        <div className="absolute top-1/3 left-[45%] w-[420px] h-[420px] rounded-full bg-[#FF1B82]/15 blur-[140px]" />
        <div className="absolute bottom-0 right-[5%] w-[460px] h-[460px] rounded-full bg-[#00E5FF]/10 blur-[150px]" />
      </div>
      <motion.div style={{ x: sx, y: sy }} className="pointer-events-none absolute w-[500px] h-[500px] rounded-full bg-ink-pink/[0.07] blur-[110px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="inline-flex items-center gap-2.5 glass rounded-full px-4 py-2 mb-8"
          >
            <span className="relative flex w-2 h-2">
              <span className="live-dot relative inline-flex rounded-full w-2 h-2 bg-ink-ember" />
            </span>
            <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.25em] text-zinc-300">LIVE SOCIAL STREAMING — GOTHENBURG, SE</span>
          </motion.div>

          <h1 data-testid="hero-heading" className="font-display font-extrabold tracking-tighter leading-[0.95] text-white text-[10vw] sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.2}>BE THERE.</MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="text-brand-gradient">TOGETHER.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
          >
            IN-It is the social app where every moment is a portal. Open an IN, go live in one tap, and let the people you want jump right into your moment — wherever they are.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-app-store-badge"
              onClick={() => scrollToId("#download")}
              className="group relative flex items-center gap-3 rounded-2xl border border-white/15 bg-black px-5 py-3 hover:border-white/40 transition-colors duration-200"
            >
              <AppleIcon className="w-7 h-7 text-white" />
              <span className="text-left">
                <span className="block text-[9px] text-zinc-400 leading-tight">Download on the</span>
                <span className="block text-base font-semibold text-white leading-tight">App Store</span>
              </span>
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] px-2.5 py-0.5 font-mono text-[8px] tracking-[0.18em] text-white">COMING SOON</span>
            </button>
            <button
              data-testid="hero-google-play-badge"
              onClick={() => scrollToId("#download")}
              className="group relative flex items-center gap-3 rounded-2xl border border-white/15 bg-black px-5 py-3 hover:border-white/40 transition-colors duration-200"
            >
              <GooglePlayIcon className="w-6 h-6" />
              <span className="text-left">
                <span className="block text-[9px] text-zinc-400 leading-tight">GET IT ON</span>
                <span className="block text-base font-semibold text-white leading-tight">Google Play</span>
              </span>
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] px-2.5 py-0.5 font-mono text-[8px] tracking-[0.18em] text-white">COMING SOON</span>
            </button>
            <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-zinc-500 ml-1">
              <span className="relative flex w-2 h-2"><span className="live-dot relative inline-flex rounded-full w-2 h-2 bg-ink-green" /></span>
              LIVE NOW · 18,420 IN THE ROOM
            </div>
          </motion.div>
        </div>

        <div className="relative">
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
};
